// ═══════════════════════════════════════════════════════════════════════════════
//  src/dojo/DojoResult.jsx
//  EL DOJO — cierre de sesión, común a los cuatro formatos
//
//  El momento donde el esfuerzo se convierte en sensación de progreso. Hoy la
//  app termina las sesiones sin decir nada (brecha documentada en la auditoría:
//  "el esfuerzo no deja huella"), y este componente es el mínimo honesto para
//  cerrarla: qué hiciste, contra qué marca, y si la rompiste.
//
//  El resultado se presenta como el `grade-display` de FLUENCY NEXUS: una cifra
//  enorme con el color del formato, y debajo las tarjetas de estadística. La
//  cifra es lo primero que se ve porque es lo único que el alumno quiere saber.
//
//  El récord es local (`localStorage`) porque hoy `save-score` recibe el
//  resultado y lo descarta — no hay tabla de marcas. Cuando exista, esto se
//  sincroniza sin tocar a los cuatro formatos.
// ═══════════════════════════════════════════════════════════════════════════════

import { useEffect, useRef, useState } from 'react';

import { fxFinish, fxRecord } from '../audio/fx';
import { getRecord, saveRecord } from './dojoFx';
import { saveDojoSession } from './dojoApi';

export default function DojoResult({
  format, score, total, headline, label = 'Resultado', recap = [], note, onAgain, onExit,
}) {
  const [prev] = useState(() => getRecord(format));
  const [isRecord, setIsRecord] = useState(false);
  // StrictMode monta dos veces en desarrollo. Sin este cerrojo, el cierre de
  // sesion guardaria el score y sumaria el XP dos veces.
  const settled = useRef(false);

  useEffect(() => {
    if (settled.current) return;
    settled.current = true;

    const beat = saveRecord(format, score);
    setIsRecord(beat);
    if (beat && prev > 0) fxRecord(); else fxFinish();

    // El esfuerzo va al progreso real del alumno, no sólo a esta pantalla.
    saveDojoSession({ format, score, correct: score, total });
    // Se ejecuta una sola vez: el resultado ya está calculado al montar.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="dj-result dj-screen">
      <p className="dj-grade">{headline}</p>
      <p className="dj-grade-lbl">{label}</p>

      {isRecord && prev > 0 && (
        <span className="dj-record">★ Récord personal — antes {prev}</span>
      )}

      {recap.length > 0 && (
        <div className="dj-recap">
          {recap.map((r) => (
            <div key={r.k}>
              <b>{r.v}</b>
              <span>{r.k}</span>
            </div>
          ))}
        </div>
      )}

      {!isRecord && prev > 0 && (
        <p className="dj-caveat" style={{ margin: '0 0 1.2rem' }}>
          Tu marca sigue en {prev}. Te faltaron {Math.max(1, prev - score + 1)} para romperla.
        </p>
      )}

      {note && <p className="dj-lead">{note}</p>}

      <div className="dj-actions">
        <button type="button" className="dj-btn" onClick={onAgain}>Otra ronda →</button>
        <button type="button" className="dj-btn dj-btn-ghost" onClick={onExit}>Volver al Dojo</button>
      </div>
    </div>
  );
}
